import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export interface TicketFormSlice {
  _id?: string;
  name?: string;
  description?: string;
  price?: number;
  quantity?: number;
  startSaleDate?: Date | string | null;
  endSaleDate?: Date | string | null;
  isRestricted?: boolean;
  hiddenFromPublicPage?: boolean;
  isActive?: boolean;
}

export type TicketSaleStatus = 'active' | 'scheduled' | 'expired' | 'inactive';

function stripTrailingSlash(url: string): string {
  return String(url || '').replace(/\/+$/, '');
}

/**
 * Base pública para links de inscrição / ingresso oculto.
 * No dashboard o host é o do painel (Meu Sacramento) — use brandKey da org
 * para montar o domínio certo (Zuadaria → zuadaria.com.br).
 */
export function getPublicBaseUrl(brandKey?: string | null): string {
  if (brandKey === 'zuadaria') {
    return stripTrailingSlash(
      process.env.REACT_APP_ZUADARIA_PUBLIC_SITE_URL || 'https://www.zuadaria.com.br'
    );
  }

  return stripTrailingSlash(
    process.env.REACT_APP_PUBLIC_SITE_URL ||
      (typeof window !== 'undefined' ? window.location.origin : '') ||
      (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '') ||
      ''
  );
}

export function buildEventPageUrl(slug: string, brandKey?: string | null): string {
  const base = getPublicBaseUrl(brandKey);
  return slug ? `${base}/${slug}` : '';
}

export function buildRegistrationUrl(
  slug: string,
  opts?: { ticketId?: string; ref?: string; brandKey?: string | null }
): string {
  const base = buildEventPageUrl(slug, opts?.brandKey);
  if (!base) return '';

  const params = new URLSearchParams();
  if (opts?.ticketId) params.set('ticket', opts.ticketId);
  if (opts?.ref?.trim()) params.set('ref', slugifyRef(opts.ref.trim()));

  const query = params.toString();
  return query ? `${base}/inscricao?${query}` : `${base}/inscricao`;
}

export function buildHiddenTicketUrl(
  slug: string,
  ticket?: TicketFormSlice | null,
  brandKey?: string | null
): string {
  const ticketId = ticket?._id;
  if (!slug?.trim() || !ticketId) return '';
  return buildRegistrationUrl(slug, { ticketId: String(ticketId), brandKey });
}

export function slugifyRef(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

export function parseTicketDate(value?: Date | string | null): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatTicketDate(value?: Date | string | null): string {
  const date = parseTicketDate(value);
  if (!date) return '—';
  return format(date, 'dd/MM/yyyy HH:mm', { locale: ptBR });
}

export function formatTicketPrice(price?: number): string {
  const value = Number(price) || 0;
  return value > 0 ? `R$ ${value.toFixed(2).replace('.', ',')}` : 'Gratuito';
}

/** Arredonda para centavos (2 casas) */
export function calculateDiscountedPrice(basePrice: number, discountPercent: number): number {
  const base = Number(basePrice) || 0;
  const pct = Math.min(100, Math.max(0, Number(discountPercent) || 0));
  const discounted = base * (1 - pct / 100);
  return Math.round(discounted * 100) / 100;
}

export function formatDiscountPreview(
  basePrice: number,
  discountPercent: number,
  finalPrice: number
): { headline: string; detail?: string; isFree: boolean } {
  const base = Number(basePrice) || 0;
  if (base <= 0) {
    return {
      headline: 'Gratuito',
      detail: 'Ingresso gratuito — desconto não altera o valor.',
      isFree: true,
    };
  }
  const pct = Math.round(Number(discountPercent) || 0);
  return {
    headline: formatTicketPrice(finalPrice),
    detail: `De ${formatTicketPrice(base)} com ${pct}% de desconto`,
    isFree: false,
  };
}

export function suggestRefCodeFromLabel(label: string): string {
  const slug = slugifyRef(label);
  return slug || `promo-${Date.now().toString(36).slice(-5)}`;
}

export function getTicketSaleStatus(ticket: TicketFormSlice): TicketSaleStatus {
  if (ticket.isActive === false) return 'inactive';

  const now = new Date();
  const start = parseTicketDate(ticket.startSaleDate);
  const end = parseTicketDate(ticket.endSaleDate);

  if (start && start > now) return 'scheduled';
  if (end && end < now) return 'expired';
  return 'active';
}

export const TICKET_STATUS_LABEL: Record<TicketSaleStatus, string> = {
  active: 'Ativo',
  scheduled: 'Agendado',
  expired: 'Encerrado',
  inactive: 'Desativado',
};

export const TICKET_STATUS_COLOR: Record<
  TicketSaleStatus,
  'success' | 'info' | 'warning' | 'default'
> = {
  active: 'success',
  scheduled: 'info',
  expired: 'warning',
  inactive: 'default',
};

export interface ReferralLinkDraft {
  id: string;
  label: string;
  refCode: string;
  /** Índice do ingresso no form; null = inscrição geral */
  ticketIndex: number | null;
  hasDiscount?: boolean;
  discountPercent?: number;
  finalPrice?: number;
  baseTicketName?: string;
  usesHiddenTicket?: boolean;
  baseTicketIndex?: number;
}

const REFERRAL_STORAGE_PREFIX = 'event-form-referral-links:v1:';

export function loadReferralLinks(slug: string): ReferralLinkDraft[] {
  if (!slug || typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(`${REFERRAL_STORAGE_PREFIX}${slug}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveReferralLinks(slug: string, links: ReferralLinkDraft[]): void {
  if (!slug || typeof window === 'undefined') return;
  window.localStorage.setItem(`${REFERRAL_STORAGE_PREFIX}${slug}`, JSON.stringify(links));
}

export async function copyToClipboard(text: string): Promise<boolean> {
  if (!text) return false;

  if (typeof navigator !== 'undefined' && window.isSecureContext && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // iOS / WebView: cai no fallback
    }
  }

  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', 'true');
    ta.setAttribute('aria-hidden', 'true');
    // iOS ignora opacity:0; precisa estar no viewport para execCommand.
    ta.style.cssText =
      'position:fixed;top:0;left:0;width:1px;height:1px;padding:0;border:0;outline:none;opacity:0.01;';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return Boolean(ok);
  } catch {
    return false;
  }
}

export interface TicketLinkMetaInput {
  label?: string;
  refCode?: string;
  ticketBatchId?: string | null;
  ticketTypeId?: string | null;
  promoTypeIndex?: number;
  hasDiscount?: boolean;
  discountPercent?: number;
  finalPrice?: number;
  baseTicketName?: string;
  baseTypeIndex?: number;
}

export interface TicketLinkDisplayInfo {
  ticketName: string;
  price: number | null;
  hasDiscount: boolean;
  discountPercent: number | null;
  priceIsFixed: boolean;
  priceBehavior: string;
}

type CatalogBatchSlice = {
  _id?: string;
  name?: string;
  price?: number;
  linkOnly?: boolean;
};

type CatalogTypeSlice = {
  name?: string;
  hiddenFromPublicPage?: boolean;
  batches?: CatalogBatchSlice[];
};

function findBatchInCatalog(
  ticketTypes: CatalogTypeSlice[],
  batchId?: string | null
): { type: CatalogTypeSlice; batch: CatalogBatchSlice } | null {
  if (!batchId) return null;
  for (const type of ticketTypes) {
    for (const batch of type.batches || []) {
      if (batch._id === batchId) return { type, batch };
    }
  }
  return null;
}

export function resolveBatchedLinkDisplay(
  link: TicketLinkMetaInput,
  ticketTypes: CatalogTypeSlice[]
): TicketLinkDisplayInfo {
  if (link.promoTypeIndex != null) {
    const type = ticketTypes[link.promoTypeIndex];
    const batch = type?.batches?.[0];
    const price = link.finalPrice ?? Number(batch?.price) ?? null;
    return {
      ticketName: link.baseTicketName || type?.name || link.label || 'Ingresso promocional',
      price,
      hasDiscount: link.hasDiscount ?? true,
      discountPercent: link.discountPercent ?? null,
      priceIsFixed: true,
      priceBehavior:
        'Preço fixo no ingresso oculto criado para este link. Se mudar o ingresso público, este link continua com o mesmo valor.',
    };
  }

  const located = findBatchInCatalog(ticketTypes, link.ticketBatchId);
  if (located) {
    const { type, batch } = located;
    const isPromo = !!type.hiddenFromPublicPage || !!batch.linkOnly;
    const price = link.finalPrice ?? Number(batch.price) ?? null;
    return {
      ticketName: link.baseTicketName || type.name || batch.name || 'Ingresso',
      price,
      hasDiscount: link.hasDiscount ?? isPromo,
      discountPercent: link.discountPercent ?? null,
      priceIsFixed: isPromo,
      priceBehavior: isPromo
        ? 'Preço fixo neste ingresso promocional. Alterar o ingresso público não muda este valor.'
        : 'Usa o mesmo ingresso da página. Se você mudar o preço na aba Ingressos, quem usar este link paga o valor novo.',
    };
  }

  return {
    ticketName: 'Inscrição geral',
    price: null,
    hasDiscount: false,
    discountPercent: null,
    priceIsFixed: false,
    priceBehavior: 'Abre a inscrição com todos os ingressos visíveis — preço conforme escolha do participante.',
  };
}

export function resolveLegacyLinkDisplay(
  link: ReferralLinkDraft & {
    hasDiscount?: boolean;
    discountPercent?: number;
    finalPrice?: number;
    baseTicketName?: string;
    usesHiddenTicket?: boolean;
  },
  tickets: TicketFormSlice[]
): TicketLinkDisplayInfo {
  if (link.ticketIndex != null) {
    const ticket = tickets[link.ticketIndex];
    const isHidden = !!(ticket?.hiddenFromPublicPage || link.usesHiddenTicket);
    const price = link.finalPrice ?? Number(ticket?.price) ?? null;
    return {
      ticketName: link.baseTicketName || ticket?.name || link.label,
      price,
      hasDiscount: link.hasDiscount ?? isHidden,
      discountPercent: link.discountPercent ?? null,
      priceIsFixed: !!isHidden,
      priceBehavior: isHidden
        ? 'Preço fixo no ingresso oculto deste link. Mudar o ingresso público não altera este valor.'
        : 'Usa o ingresso normal. Se mudar o preço na aba Ingressos, quem usar este link paga o valor novo.',
    };
  }

  return {
    ticketName: 'Inscrição geral',
    price: null,
    hasDiscount: false,
    discountPercent: null,
    priceIsFixed: false,
    priceBehavior: 'Abre a inscrição geral — preço conforme ingresso escolhido.',
  };
}

export function formatLinkPriceSummary(info: TicketLinkDisplayInfo): string {
  if (info.price == null) return 'Preço variável';
  if (info.hasDiscount && info.discountPercent) {
    return `${formatTicketPrice(info.price)} (${info.discountPercent}% off)`;
  }
  return formatTicketPrice(info.price);
}
